import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Install-to-Home-Screen banner (Feb 2026).
 *
 * Catches the browser's `beforeinstallprompt` event (Chrome / Edge /
 * Android — the only browsers that support the programmatic install
 * flow) and surfaces a friendly one-tap banner. Falls back to a short
 * instructional line for iOS Safari, which requires the user to use the
 * Share menu → "Add to Home Screen" (Apple doesn't let websites trigger
 * the install prompt programmatically).
 *
 * Dismissal is remembered for 7 days per browser so we don't nag every
 * page load — but we WILL show again after 7 days, in case the user
 * changed their mind. Once installed (`display-mode: standalone`), the
 * banner never shows.
 *
 * Placed near the bottom of the viewport so it doesn't fight the
 * VersionWatcher (which uses a fixed bottom-center position). Install
 * banner sits above it, both stacked cleanly.
 */
const DISMISS_KEY = "towhook.install.dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iPadOS 13+ reports as Macintosh — sniff for touch to catch it.
  const isTouchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || isTouchMac;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // Chrome / Android / installed PWA
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari installed-to-home-screen (legacy but still supported)
  if (window.navigator.standalone === true) return true;
  return false;
}

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Never show if the app is already installed / running standalone
    if (isStandalone()) return;
    if (isRecentlyDismissed()) return;

    // iOS Safari — no beforeinstallprompt event; show a hint instead
    // (only if the user is actually in Safari, not Chrome-on-iOS which
    // can't install PWAs at all).
    if (isIOS()) {
      const ua = navigator.userAgent || "";
      const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
      if (isSafari) {
        // Delay a beat so the hint doesn't fight the login page render
        const t = setTimeout(() => setShowIOSHint(true), 2500);
        return () => clearTimeout(t);
      }
      return;
    }

    // Chrome / Edge / Android — programmatic install available
    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // Also listen for `appinstalled` so we hide the banner immediately
    // once the user completes the install flow (rather than waiting for
    // the next page load).
    const onInstalled = () => {
      setDeferredPrompt(null);
      setShowIOSHint(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSHint(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    // Chrome resolves this promise with `{ outcome: 'accepted' | 'dismissed' }`
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* noop */ }
    // Whatever the user chose, we can't use the same event twice.
    setDeferredPrompt(null);
    // If they dismissed via the native prompt, respect that for the TTL.
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
  };

  if (dismissed) return null;

  // Native install available (Chrome / Edge / Android)
  if (deferredPrompt) {
    return (
      <div
        // 6rem above the version watcher so both stack cleanly when they
        // fire together (rare but possible during a redeploy on a
        // never-installed browser).
        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[99] max-w-md w-[92%] rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/90 dark:border-emerald-700 shadow-lg px-4 py-3 flex items-center gap-3"
        data-testid="install-prompt-banner"
      >
        <Download className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            Install Tow-Hook
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300">
            Adds an icon to your home screen so you can open it like a real app.
          </div>
        </div>
        <Button
          size="sm"
          onClick={install}
          className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
          data-testid="install-prompt-install-btn"
        >
          Install
        </Button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1 -m-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          aria-label="Dismiss"
          data-testid="install-prompt-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // iOS Safari — show the manual instruction hint
  if (showIOSHint) {
    return (
      <div
        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[99] max-w-md w-[92%] rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/90 dark:border-emerald-700 shadow-lg px-4 py-3 flex items-center gap-3"
        data-testid="install-prompt-ios"
      >
        <Download className="h-5 w-5 text-emerald-600 dark:text-emerald-300 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            Add Tow-Hook to your home screen
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300">
            Tap the <b>Share</b> button below, then choose <b>&ldquo;Add to Home Screen&rdquo;</b>.
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="p-1 -m-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          aria-label="Dismiss"
          data-testid="install-prompt-ios-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
}
